import { Component, Input, OnInit } from '@angular/core'
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup
} from '@angular/forms'
import { Store } from '@ngrx/store'
import { combineLatest, Observable } from 'rxjs'
import { Product } from 'src/app/features/product/product'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { SaleItem } from '../sale-item'
import { Dictionary } from '@ngrx/entity'
import { bookingSelectors } from 'src/app/features/booking/booking.selectors'
import { Booking } from 'src/app/features/booking/booking'
import { ConfigurationService } from '../../../../shared/services/configuration.service'
import {
  ParticipantsAdjustmentAction,
  Participation
} from 'src/app/features/booking/participation/participation'
import {
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { groupBy } from 'src/app/shared/until'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { participationActions } from 'src/app/features/booking/participation/participation.actions'
import { environment } from 'src/environments/environment'
import { productActions } from 'src/app/features/product/product.actions'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { AppState } from 'src/app/core/core.state'
import { ParticipationService } from 'src/app/features/booking/participation/participation.service'
import { bookingActions } from 'src/app/features/booking/booking.actions'
import moment from 'moment-mini'

@UntilDestroy()
@Component({
  selector: 'sale-item-participants-form',
  templateUrl: './participants-form.component.html',
  styleUrls: ['./participants-form.component.scss']
})
export class SaleItemParticipantsFormComponent implements OnInit {
  @Input() saleItem: Partial<SaleItem>
  @Input() participants$: Observable<Participation[]>

  public parentBookableProducts$: Observable<Product[]>
  public bookingEntities$: Observable<Dictionary<Booking>>
  public productEntities$: Observable<Dictionary<Product>>
  public participantsByBookingId$: Observable<Dictionary<Participation[]>>
  public form: UntypedFormGroup
  public saleModuleConfig = environment.moduleConfigs.saleModule
  public operationType: string
  public isTableCollapsed: boolean = true
  private bookings: Booking[] = []
  constructor(
    private fb: UntypedFormBuilder,
    private store: Store<AppState>,
    public configurationService: ConfigurationService,
    public participationService: ParticipationService
  ) {}

  public ngOnInit() {
    this.form = this.fb.group({
      participantRows: new UntypedFormArray([])
    })

    if (this.saleItem.productId) {
      this.store.dispatch(
        productActions.fetchOneById({
          id: this.saleItem.productId,
          params: RequestQueryBuilder.create({
            join: [
              { field: 'tags' },
              { field: 'parentBookableProducts' },
              { field: 'parentBookableProducts.tags' },
              { field: 'parentBookableProducts.translations' }
            ]
          })
        })
      )
    }

    this.bookingEntities$ = this.store.select(bookingSelectors.selectEntities)

    this.store.select(bookingSelectors.selectAll).subscribe((bookings) => {
      this.bookings = bookings
    })

    this.productEntities$ = this.store.select(productSelectors.selectEntities)

    this.parentBookableProducts$ = this.productEntities$.pipe(
      filter(() => Boolean(this.saleItem?.productId)),
      map((productEntities) =>
        productEntities[this.saleItem.productId]?.parentBookableProducts
          ?.map((parentId) => productEntities[parentId])
          .filter((product) => product.isActive)
      ),
      startWith([])
    )

    this.parentBookableProducts$.subscribe((x) => {
      if (x?.length)
        this.store.dispatch(
          bookingActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                productId: {
                  [CondOperator.IN]: x.map((p) => p.id)
                },
                startDate: {
                  [CondOperator.BETWEEN]: [
                    moment(
                      this.bookings.find((b) =>
                        x.map((x) => x.id).includes(b.productId)
                      ).startDate
                    )
                      .startOf('day')
                      .toISOString(),
                    moment(
                      this.bookings.find((b) =>
                        x.map((x) => x.id).includes(b.productId)
                      ).startDate
                    )
                      .endOf('day')
                      .toISOString()
                  ]
                }
              }
            })
          })
        )
    })

    this.participants$
      .pipe(
        untilDestroyed(this),
        map(
          (participants) =>
            <Dictionary<Participation[]>>(
              groupBy(participants, (p) => p.bookingId)
            )
        ),
        withLatestFrom(this.bookingEntities$),
        tap(([participantsByBookingId, bookingEntities]) => {
          Object.keys(participantsByBookingId)
            .filter(Boolean)
            .map((bookingId) => {
              participantsByBookingId[bookingId]
                .filter((p) => p.saleItemId == this.saleItem.id)
                .filter(
                  (p) =>
                    !this.form.value?.participantRows?.find(
                      (participantRow) => participantRow.participationId == p.id
                    )
                )
                .map((participant) => {
                  const booking = bookingEntities[bookingId]

                  const participantIsAlreadyAdded = Boolean(
                    this.participantRows?.value.find(
                      (participantRow) =>
                        participantRow.participationId === participant.id
                    )
                  )

                  if (!participantIsAlreadyAdded) {
                    this.participantRows.push(
                      this.fb.group({
                        bookingId: [parseInt(bookingId), []],
                        type: [participant.type, []],
                        participationId: [participant.id, []],
                        targetProductId: [booking?.productId],
                        action: 'normal'
                      })
                    )
                  }
                })
            })
        })
      )
      .subscribe()
  }

  public get participantRows(): UntypedFormArray {
    return this.form.get('participantRows') as UntypedFormArray
  }

  public participantRowsFormGroupAt(i: number) {
    return this.participantRows.at(i) as UntypedFormGroup
  }

  public addNewRow(): void {
    this.participants$
      .pipe(take(1), withLatestFrom(this.bookingEntities$))
      .subscribe(([participants, bookingEntities]) => {
        const booking = bookingEntities[participants[0]?.bookingId]

        this.participantRows.push(
          this.fb.group({
            type: [null, []],
            participationId: [null, []],
            targetProductId: [null, []],
            action: ParticipantsAdjustmentAction.ADD,
            destinationBookingId: [null, []],
            startDate: [booking?.startDate, []],
            endDate: [booking?.endDate, []],
            targetLinkedProductId: [booking?.linkedProductId, []]
          })
        )
      })
  }

  public onDeleteRow(formRowIndex) {
    this.participantRowsFormGroupAt(formRowIndex).controls.action.patchValue(
      ParticipantsAdjustmentAction.DELETE
    )

    this.store.dispatch(
      participationActions.removeOne({
        id: this.participantRowsFormGroupAt(formRowIndex).controls
          .participationId.value
      })
    )
  }

  public onRestoreRow(formRowIndex) {
    this.participantRowsFormGroupAt(formRowIndex).controls.action.patchValue(
      'normal'
    )
  }

  public getIsOverBooked(participation: Participation): Observable<boolean> {
    return this.store
      .select(bookingSelectors.selectById, participation.bookingId)
      .pipe(map((booking) => booking.availableParticipantCount < 0))
  }

  public isBookingFull$(): Observable<boolean> {
    return combineLatest([
      this.participants$,
      this.bookingEntities$,
      this.productEntities$,
      this.store.select(bookingSelectors.selectAll)
    ]).pipe(
      map(([participants, bookingEntities, productEntities, bookings]) => {
        const booking = bookingEntities[participants[0]?.bookingId]

        if (!booking) {
          return false
        }

        const targetProduct = booking.linkedProductId
          ? productEntities[booking.linkedProductId]
          : productEntities[booking.productId]

        if (targetProduct.allowChooseCategory) {
          return (
            this.participantRows.length - participants.length >=
            booking.availableParticipantCount
          )
        }

        if (targetProduct?.spreadParticipantsOverResources) {
          let similarBookableSlots = bookings.filter((bookableSlot) => {
            return (
              ((!booking?.linkedProductId &&
                targetProduct.parentBookableProducts.includes(
                  bookableSlot?.linkedProductId
                )) ||
                bookableSlot?.linkedProductId == booking?.linkedProductId) &&
              bookableSlot?.startDate == booking?.startDate
            )
          })
          let totalMaxParticipants = similarBookableSlots.reduce((total, b) => {
            return (total +=
              b.availableParticipantCount + b.currentParticipantCount)
          }, 0)

          return (
            participants.length >= totalMaxParticipants ||
            this.participantRows.length >= totalMaxParticipants
          )
        }

        let totals = this.participationService.calculateParticipantLimits(
          productEntities[booking.productId],
          productEntities[booking.linkedProductId],
          booking.maxParticipants
        )

        return (
          booking.availableParticipantCount <= 0 ||
          this.participantRows.length >= totals.maxParticipants
        )
      })
    )
  }
}
